import React, { useState, useEffect, useCallback } from 'react'
import styles from './ImageViewer.module.scss'
import { JWayIcon } from '../../../assets/JWayIcons'


export interface ImageViewerProps {
    images: ImageData[]
    currentIndex: number
    isOpen: boolean
    onClose: () => void
    onNavigate?: (index: number) => void
}

export interface ImageData {
    id: string
    src: string
    name: string
    type: string
}

export default function ImageViewer({ 
    images, 
    currentIndex, 
    isOpen, 
    onClose, 
    onNavigate 
}: ImageViewerProps) {
    const [scale, setScale] = useState(1)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
    const [imageLoaded, setImageLoaded] = useState(false)

    const currentImage = images[currentIndex]

    // Reset zoom and position when image changes
    useEffect(() => {
        setScale(1)
        setPosition({ x: 0, y: 0 })
        setImageLoaded(false)
    }, [currentIndex])

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!isOpen) return

        switch (e.key) {
            case 'Escape':
                onClose()
                break
            case 'ArrowLeft':
                if (currentIndex > 0 && onNavigate) {
                    onNavigate(currentIndex - 1)
                }
                break
            case 'ArrowRight':
                if (currentIndex < images.length - 1 && onNavigate) {
                    onNavigate(currentIndex + 1)
                }
                break
            case '+':
            case '=':
                e.preventDefault()
                handleZoomIn()
                break
            case '-':
                e.preventDefault()
                handleZoomOut()
                break
            case '0':
                e.preventDefault()
                handleResetZoom()
                break
        }
    }, [isOpen, currentIndex, images.length, onNavigate, onClose])

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'auto'
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'auto'
        }
    }, [isOpen, handleKeyDown])

    const handleZoomIn = () => {
        setScale(prev => Math.min(prev * 1.5, 5))
    }

    const handleZoomOut = () => {
        setScale(prev => Math.max(prev / 1.5, 0.5))
    }

    const handleResetZoom = () => {
        setScale(1)
        setPosition({ x: 0, y: 0 })
    }

    const handleDownload = async () => {
        try {
            // Si c'est une data URL, on peut télécharger directement
            if (currentImage.src.startsWith('data:')) {
                const link = document.createElement('a')
                link.href = currentImage.src
                link.download = currentImage.name || 'image'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                return
            }

            // Pour les URLs externes, on utilise fetch pour éviter les problèmes CORS
            const response = await fetch(currentImage.src)
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            
            const link = document.createElement('a')
            link.href = url
            link.download = currentImage.name || 'image'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            
            // Nettoyer l'URL object
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error)
            // Fallback: essayer la méthode simple
            const link = document.createElement('a')
            link.href = currentImage.src
            link.download = currentImage.name || 'image'
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    const handleMouseDown = (e: React.MouseEvent) => {
        if (scale > 1) {
            setIsDragging(true)
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            })
        }
    }

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            })
        }
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }



    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose()
        }
    }

    const handlePrevious = () => {
        if (currentIndex > 0 && onNavigate) {
            onNavigate(currentIndex - 1)
        }
    }

    const handleNext = () => {
        if (currentIndex < images.length - 1 && onNavigate) {
            onNavigate(currentIndex + 1)
        }
    }

    // Debug logs
    console.log('ImageViewer render:', { isOpen, currentIndex, imagesLength: images.length, currentImage })

    if (!isOpen || !currentImage) {
    
        return null
    }

    return (
        <div className={styles.overlay} onClick={handleBackdropClick}>
            <div className={styles.viewer}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.info}>
                        <h3 className={styles.filename}>{currentImage.name}</h3>
                        <span className={styles.counter}>
                            {currentIndex + 1} / {images.length}
                        </span>
                    </div>
                    <button 
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Fermer"
                    >
                        ×
                    </button>
                </div>

                {/* Image Container */}
                <div 
                    className={styles.imageContainer}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {!imageLoaded && (
                        <div className={styles.loading}>
                            Chargement...
                        </div>
                    )}
                    <img
                        src={currentImage.src}
                        alt={currentImage.name}
                        className={styles.image}
                        style={{
                            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                        }}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageLoaded(true)}
                        draggable={false}
                    />
                </div>

                {/* Navigation */}
                {images.length > 1 && (
                    <>
                        <button
                            type="button"
                            className={`${styles.navButton} ${styles.navButton_prev}`}
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                            aria-label="Image précédente"
                        >
                            ‹
                        </button>
                        <button
                            type="button"
                            className={`${styles.navButton} ${styles.navButton_next}`}
                            onClick={handleNext}
                            disabled={currentIndex === images.length - 1}
                            aria-label="Image suivante"
                        >
                            ›
                        </button>
                    </>
                )}

                {/* Controls */}
                <div className={styles.controls}>
                    <button
                        type="button"
                        className={styles.controlButton}
                        onClick={handleZoomOut}
                        disabled={scale <= 0.5}
                        aria-label="Zoom arrière"
                    >
                        −
                    </button>
                    <span className={styles.zoomLevel}>
                        {Math.round(scale * 100)}%
                    </span>
                    <button
                        type="button"
                        className={styles.controlButton}
                        onClick={handleZoomIn}
                        disabled={scale >= 5}
                        aria-label="Zoom avant"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className={styles.controlButton}
                        onClick={handleResetZoom}
                        aria-label="Réinitialiser zoom"
                    >
                        ⌂
                    </button>
                    <button
                        type="button"
                        className={styles.controlButton}
                        onClick={handleDownload}
                        aria-label="Télécharger l'image"
                    >
                        <JWayIcon type="download" size={20} color="currentColor" />
                    </button>
                </div>

                {/* Thumbnail strip for multiple images */}
                {images.length > 1 && (
                    <div className={styles.thumbnails}>
                        {images.map((image, index) => (
                            <button
                                key={image.id}
                                type="button"
                                className={`${styles.thumbnail} ${
                                    index === currentIndex ? styles.thumbnail_active : ''
                                }`}
                                onClick={() => onNavigate?.(index)}
                            >
                                <img
                                    src={image.src}
                                    alt={image.name}
                                    className={styles.thumbnailImage}
                                />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
